import { type ReactNode } from 'react';
import { useFeatureFlags, type FeatureFlags } from '../context/useFeatureFlags';
import { usePagePermissions } from '../context/usePagePermissions';
import { usePermissions, type ClinicMgmtPermission } from '../context/usePermissions';
import NotFoundPage from '../../pages/NotFoundPage';
import AccessDeniedPage from '../../pages/AccessDeniedPage';

interface AuthorizedRouteProps {
  flag?: keyof FeatureFlags;
  pageKey?: string;
  /** Single new-style verb permission required. */
  permission?: ClinicMgmtPermission;
  /** User must hold at least one of these new-style verb permissions. */
  anyOf?: ClinicMgmtPermission[];
  children: ReactNode;
}

/**
 * Route-level guard. The legacy `pageKey` check and the new verb-permission
 * check are combined with OR logic — if either grants access the route is
 * shown. This lets the page-permission system keep working for users who
 * haven't yet been migrated to the new ItemPropertyTypes-based grants.
 * SysAdmin always passes via usePermissions().
 */
export default function AuthorizedRoute({
  flag,
  pageKey,
  permission,
  anyOf,
  children,
}: Readonly<AuthorizedRouteProps>) {
  const flags = useFeatureFlags();
  const { hasPageAccess, isLoading: pagePermsLoading, pages } = usePagePermissions();
  const { hasPermission, hasAnyPermission, isSysAdmin } = usePermissions();

  // Feature flag disabled -> 404 (same as FeatureRoute)
  if (flag && !flags[flag]) return <NotFoundPage />;

  // With the security feature disabled the app runs open: the legacy
  // page-permission system allows every route, so the verb-permission
  // checks below must not deny access either.
  if (!flags.security) return <>{children}</>;

  const hasPermissionRequirement = !!(permission || anyOf?.length);

  // If no permission requirement is declared, fall back to the legacy pageKey check alone.
  if (!hasPermissionRequirement) {
    if (pageKey && !hasPageAccess(pageKey)) return <AccessDeniedPage />;
    return <>{children}</>;
  }

  // Otherwise, allow if SysAdmin, OR a verb permission passes, OR the legacy pageKey
  // is *explicitly* granted in the loaded pages map. We deliberately do NOT use
  // hasPageAccess() here because it returns true while page-perm data is still
  // loading (and for unknown keys), which would let an unauthorized user briefly
  // bypass the new verb check on first render.
  const permissionAllowed = isSysAdmin
    || (permission ? hasPermission(permission) : false)
    || (anyOf?.length ? hasAnyPermission(...anyOf) : false);
  const pageKeyExplicitlyAllowed =
    !!pageKey && !pagePermsLoading && pages[pageKey] === true;

  if (!permissionAllowed && !pageKeyExplicitlyAllowed) return <AccessDeniedPage />;

  return <>{children}</>;
}
